import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { match } from '@/utils/pattern-match-util'
import styles from './DocumentItemActionsDropdown.module.css'

export interface DocumentActions {
  onView: () => unknown
  onDownload: () => unknown
  onUnlink?: () => unknown
  onDelete?: () => unknown
}

export interface DocumentItemActionsDropdownProps {
  actions: DocumentActions
  /**
   * The look of the trigger button.
   */
  variant?: 'default' | 'ghost'
  /**
   * Keeps item presses from reaching the element around the dropdown.
   */
  muteClickEvent?: boolean
  /**
   * The document title, which the test IDs are composed from.
   */
  title: string
}

/**
 * The "More" menu of actions on a document.
 */
export function DocumentItemActionsDropdown({
  actions,
  variant = 'default',
  muteClickEvent,
  title,
}: DocumentItemActionsDropdownProps) {
  const triggerTestId = `download-actions-dropdown-${title}`

  return (
    <Dropdown
      width="medium"
      autoDismiss
      position="bottom-end"
      button={match(variant, {
        default: () => (
          <span className={styles.hover}>
            <IconButton
              name="more"
              label="Document actions"
              testId={triggerTestId}
            />
          </span>
        ),
        ghost: () => (
          <IconButton
            name="more"
            label="Document actions"
            variant="ghost"
            size={16}
            testId={triggerTestId}
          />
        ),
      })}
    >
      <DropdownTitle>More</DropdownTitle>
      <DropdownItem
        icon={<Icon name="view" />}
        muteClickEvent={muteClickEvent}
        onPress={actions.onView}
      >
        View
      </DropdownItem>
      <DropdownItem
        icon={<Icon name="download" />}
        muteClickEvent={muteClickEvent}
        testId={`download-actions-dropdown-item-download-${title}`}
        onPress={actions.onDownload}
      >
        Download
      </DropdownItem>
      {actions.onUnlink && (
        <DropdownItem
          icon={<Icon name="link" />}
          muteClickEvent={muteClickEvent}
          onPress={actions.onUnlink}
        >
          Unlink
        </DropdownItem>
      )}
      {actions.onDelete && (
        <DropdownItem
          icon={<Icon name="delete" />}
          muteClickEvent={muteClickEvent}
          onPress={actions.onDelete}
        >
          Delete
        </DropdownItem>
      )}
    </Dropdown>
  )
}
